import {ffLazyLoading, hasFeatureFlag, logger} from 'yougile-util'
import {Hub} from 'yougile-model'
import {Sync} from '@client/sync'
import {Tracking, TrackingEvent} from '@common/tracking'

const memoryUsage: number[] = []
const MIN_MEMORY_USAGE_HISTORY = 60
const MAX_MEMORY_USAGE_HISTORY = 300
const isMemoryUsageAvailable =
  'performance' in window &&
  'memory' in performance &&
  'usedJSHeapSize' in performance.memory

const state = {
  indexLoadTime: 0
}

export const registerIndexLoadDuration = (time: number): void => {
  state.indexLoadTime = time
}

export const tryToSendPerformanceEvent = (): void => {
  try {
    if (!isMemoryUsageAvailable) {
      return
    }

    const lastSend = localStorage.getItem('performanceEvent__lastSend')
    if (lastSend && Date.now() - Number(lastSend) < 24 * 60 * 60 * 1000) {
      return
    }

    if (memoryUsage.length < MIN_MEMORY_USAGE_HISTORY) {
      logger.log(
        `Not enough memory usage history, ${memoryUsage.length} of ${MIN_MEMORY_USAGE_HISTORY}`
      )
      return
    }

    const maxMemory = Math.max(...memoryUsage)
    const avgMemory =
      memoryUsage.reduce((acc, val) => acc + val, 0) / memoryUsage.length

    const box = Sync.getCurrentSandbox()
    const totalEntities = box.getEntitiesCount()
    const lazyEntities = box.getLazyEntitiesCount()

    const totalMessages = Object.values(box.entities).reduce(
      (total, entity) => {
        return total + ((entity as Hub).chat?.messages.size ?? 0)
      },
      0
    )

    const isLazyModeEnabled = hasFeatureFlag(ffLazyLoading)

    const eventData = {
      maxMemory,
      avgMemory,
      totalEntities,
      lazyEntities,
      isLazyModeEnabled,
      indexLoadTime: state.indexLoadTime,
      totalMessages
    }

    Tracking.send(TrackingEvent.performanceStats, eventData)
    localStorage.setItem('performanceEvent__lastSend', String(Date.now()))
  } catch (e) {
    logger.error('Failed to send performance event', e)
  }
}

declare global {
  interface Performance {
    memory: {
      usedJSHeapSize: number
    }
  }
}

export const startMemoryUpdateLoop = (): void => {
  if (!isMemoryUsageAvailable) {
    return
  }

  setInterval(() => {
    try {
      const currentMemory =
        performance.memory.usedJSHeapSize / 1024 / 1024 / 1024
      memoryUsage.push(currentMemory)
      if (memoryUsage.length > MAX_MEMORY_USAGE_HISTORY) {
        memoryUsage.shift()
      }
    } catch {
      // ignore
    }
  }, 2000)
}
